// src/components/SPanel/Home/HomeWidgets.jsx
//
// Widget-urile paginii principale a elevului, fiecare în mărimile lui
// (1×1, 2×1, 2×2 — vezi homeLayout.js). Primesc datele gata încărcate și știu
// să arate „gol” sau „se încarcă”. renderHomeWidget alege widget-ul după id.

import React from "react";
import { Link } from "react-router-dom";

import ProgressSegments from "../ProgressSegments";
import LessonsCarousel from "./LessonsCarousel";
import { ActivityWidget, ProgressWidget } from "./ActivityWidgets";
import {
   ExamTrendWidget,
   ExamsWidget,
   GoalWidget,
   MistakesWidget,
   NextLessonWidget,
   NotificationsWidget,
} from "./MoreWidgets";
import { WeatherWidget } from "./WeatherWidget";
import { CountUp, Head, Icon, Skeleton, Tile } from "./HomeTile";
import { useAppeared, useCountUp } from "./homeHooks";
import { openPopup } from "../../Common/popupUIStore";
import { answersAccuracy, groupByState } from "../../../utils/practiceStatsSummary";
import { formatCountdown, formatDayMonth, formatDriven, formatTime, roCount } from "./homeLessons";
import { youtubeThumbnailUrl } from "../../../utils/youtube";

import calendarIcon from "../../../assets/svg/mdi--calendar-outline.svg";
import addIcon from "../../../assets/svg/mdi--calendar-plus-outline.svg";
import bookIcon from "../../../assets/svg/material-symbols--book-outline.svg";
import examIcon from "../../../assets/svg/mdi--book-clock-outline.svg";
import carIcon from "../../../assets/svg/material-symbols--car-tag-outline.svg";
import chartIcon from "../../../assets/svg/mdi--chart-line.svg";
import playIcon from "../../../assets/svg/mdi--play-circle-outline.svg";
import messageIcon from "../../../assets/svg/tabler--message.svg";

const CHECK_PATH = "M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z";
const PLAY_PATH = "M8 5.14v14l11-7z";

/* ================= bun venit ================= */

function welcomeText(track, unread, now) {
   const { stats, next } = track;
   const lines = [
      stats.done > 0
         ? `Ai făcut ${roCount(stats.done, "lecție", "lecții")} din ${stats.total}.`
         : "Drumul tău spre permis începe aici.",
   ];
   if (next) {
      lines.push(
         next.state === "live"
            ? "Acum ești la lecție, drum bun!"
            : `Următoarea lecție e ${formatCountdown(next.start, next.end, now)}, ${formatDayMonth(next.start).weekday} la ${formatTime(next.start)}.`,
      );
   } else if (stats.todo > 0) {
      lines.push("Nu ai nicio lecție programată, alege-ți următoarea.");
   }
   if (unread > 0) lines.push(`Ai ${roCount(unread, "notificare nouă", "notificări noi")}.`);
   return lines.join(" ");
}

const ACTIONS = [
   { key: "book", text: "Programează", icon: addIcon, popup: "sAddProg", primary: true },
   { key: "test", text: "Testare", icon: bookIcon, to: "/student/test" },
   { key: "exam", text: "Ex. teoretic", icon: examIcon, to: "/student/exam" },
   { key: "chat", text: "Mesaje", icon: messageIcon, popup: "staffChat" },
   { key: "practical", text: "Ex. practic", icon: carIcon, popup: "practicalExamBooking" },
   { key: "video", text: "Video", icon: playIcon, to: "/student/videoclipuri" },
];

function ActionButton({ action, className }) {
   const cls = `${className}${action.primary ? ` ${className}--primary` : ""}`;
   const content = (
      <>
         <span className={`${className}__icon`}>
            <Icon src={action.icon} />
         </span>
         <span className={`${className}__text`}>{action.text}</span>
      </>
   );
   return action.to ? (
      <Link to={action.to} className={cls}>
         {content}
      </Link>
   ) : (
      <button type="button" className={cls} onClick={() => openPopup(action.popup)}>
         {content}
      </button>
   );
}

function WelcomeWidget({ size, data }) {
   const { user, track, unread, now } = data;
   return (
      <Tile id="welcome" size={size}>
         <span className="wd-welcome__glow" aria-hidden="true" />
         <h2 className="wd-welcome__title">
            Bună, <span className="wd-welcome__name">{user?.firstName || "..."}</span>
         </h2>
         <p className="wd-welcome__lead">{welcomeText(track, unread, now)}</p>
         {/* butoanele rapide apar doar când widget-ul are loc (vezi .wd-chips) */}
         <div className="wd-chips">
            {ACTIONS.slice(0, 5).map((action) => (
               <ActionButton key={action.key} action={action} className="wd-chip" />
            ))}
         </div>
      </Tile>
   );
}

function ActionsWidget({ size }) {
   if (size === "1x1") {
      const book = ACTIONS[0];
      return (
         <Tile id="actions" size={size} tone="accent" onClick={() => openPopup(book.popup)} label="Programează o lecție">
            <span className="wd-bigaction__icon">
               <Icon src={book.icon} />
            </span>
            <span className="wd-bigaction__text">Programează</span>
            <span className="wd-bigaction__sub">o lecție</span>
         </Tile>
      );
   }
   const list = size === "2x2" ? ACTIONS : ACTIONS.slice(0, 4);
   return (
      <Tile id="actions" size={size}>
         {size === "2x2" && <Head icon={addIcon} title="Acțiuni rapide" />}
         <div className="wd-actions">
            {list.map((action) => (
               <ActionButton key={action.key} action={action} className="wd-action" />
            ))}
         </div>
      </Tile>
   );
}

/* ================= drumul: caruselul lecțiilor ================= */

function JourneyWidget({ size, data }) {
   const { track } = data;
   const openLesson = (item) => {
      if (!item) return;
      if (item.state === "todo") openPopup("sAddProg");
      else openPopup("eventInfo", { event: item.event });
   };
   return (
      <Tile id="journey" size={size}>
         {/* antetul se ascunde singur pe widget-urile scunde (vezi .wd--journey) */}
         <Head
            icon={calendarIcon}
            title="Drumul tău"
            extra={
               <>
                  <span className="wd__muted">
                     {track.stats.done} / {track.stats.total}
                  </span>
                  <span className="wd-journey__legend" aria-hidden="true">
                     <span className="wd-key wd-key--done">Efectuate</span>
                     <span className="wd-key wd-key--next">Următoarea</span>
                     <span className="wd-key wd-key--scheduled">Programate</span>
                     <span className="wd-key wd-key--todo">De programat</span>
                  </span>
               </>
            }
         />
         <LessonsCarousel items={track.items} focusIndex={track.focusIndex} onOpenLesson={openLesson} />
      </Tile>
   );
}

/* ================= lecții ================= */

function LessonsBar({ stats, appeared }) {
   return (
      <div className="wd-lessonbar" role="img" aria-label={`${stats.done} efectuate, ${stats.scheduled} programate din ${stats.total}`}>
         <span className="wd-lessonbar__done" style={{ width: `${appeared ? (100 * stats.done) / stats.total : 0}%` }} />
         <span className="wd-lessonbar__scheduled" style={{ width: `${appeared ? (100 * stats.scheduled) / stats.total : 0}%` }} />
      </div>
   );
}

function LessonsWidget({ size, data }) {
   const { stats, items } = data.track;
   const appeared = useAppeared(200);
   const big = (
      <div className="wd-big">
         <span className="wd-big__num">
            <CountUp value={stats.done} />
         </span>
         <span className="wd-big__of">/ {stats.total}</span>
         {size !== "1x1" && <span className="wd-big__unit">efectuate</span>}
      </div>
   );
   const facts = (
      <div className="wd-facts">
         <div className="wd-fact">
            <span>Programate</span>
            <strong>
               <CountUp value={stats.scheduled} />
            </strong>
         </div>
         <div className="wd-fact">
            <span>De programat</span>
            <strong>
               <CountUp value={stats.todo} />
            </strong>
         </div>
         <div className="wd-fact">
            <span>La volan</span>
            <strong>{formatDriven(stats.drivenMinutes)}</strong>
         </div>
      </div>
   );

   if (size === "1x1") {
      return (
         <Tile id="lessons" size={size} to="/student/calendar" label={`Lecții: ${stats.done} din ${stats.total}`}>
            <Head icon={chartIcon} title="Lecții" />
            {big}
            <LessonsBar stats={stats} appeared={appeared} />
            <span className="wd-caption">efectuate</span>
            <div className="wd-roomy wd-roomy--grid">{facts}</div>
         </Tile>
      );
   }

   if (size === "2x1") {
      return (
         <Tile id="lessons" size={size}>
            <Head icon={chartIcon} title="Lecții" action={{ to: "/student/calendar", label: "Calendar" }} />
            <div className="wd-split">
               <div className="wd-split__main">
                  {big}
                  <LessonsBar stats={stats} appeared={appeared} />
               </div>
               {facts}
            </div>
         </Tile>
      );
   }

   return (
      <Tile id="lessons" size={size}>
         <Head icon={chartIcon} title="Lecții" action={{ to: "/student/calendar", label: "Calendar" }} />
         {big}
         <div
            className={`wd-cells${appeared ? " is-in" : ""}`}
            role="img"
            aria-label={`${stats.done} efectuate, ${stats.scheduled} programate, ${stats.todo} de programat`}
         >
            {items.map((item, index) => (
               <span
                  key={item.key}
                  className={`wd-cells__cell wd-cells__cell--${item.state}${item.isNext ? " wd-cells__cell--next" : ""}`}
                  style={{ "--k": index }}
                  title={`Lecția ${item.number}`}
               />
            ))}
         </div>
         {facts}
      </Tile>
   );
}

/* ================= întrebări: acuratețea ================= */

const RING_R = 52;
const RING_C = 2 * Math.PI * RING_R;

function Ring({ percent, answered, appeared }) {
   const shown = useCountUp(percent ?? 0, 1300);
   const fill = appeared && percent != null ? percent / 100 : 0;
   return (
      <div className="wd-ring" role="img" aria-label={`Acuratețe ${percent ?? 0}%`}>
         <svg viewBox="0 0 120 120">
            <circle className="wd-ring__track" cx="60" cy="60" r={RING_R} />
            <circle
               className="wd-ring__bad"
               cx="60"
               cy="60"
               r={RING_R}
               strokeDasharray={RING_C}
               strokeDashoffset={appeared && answered ? 0 : RING_C}
            />
            <circle
               className="wd-ring__ok"
               cx="60"
               cy="60"
               r={RING_R}
               strokeDasharray={RING_C}
               strokeDashoffset={RING_C * (1 - fill)}
            />
         </svg>
         <span className="wd-ring__label">
            <strong>{percent == null ? "—" : `${shown}%`}</strong>
            <small>acuratețe</small>
         </span>
      </div>
   );
}

function AccuracyWidget({ size, data }) {
   const { practice } = data;
   const appeared = useAppeared(320);
   const { correct, wrong, answered, percent } = answersAccuracy(practice?.summary);

   if (!practice) {
      return (
         <Tile id="accuracy" size={size}>
            <Head icon={examIcon} title="Întrebări" />
            <Skeleton />
         </Tile>
      );
   }

   if (size === "1x1") {
      return (
         <Tile id="accuracy" size={size} to="/student/test" label={`Acuratețe ${percent ?? 0}%`}>
            <Head icon={examIcon} title="Întrebări" />
            <div className="wd-variant wd-variant--small wd-center">
               <Ring percent={percent} answered={answered} appeared={appeared} />
            </div>
            <div className="wd-variant wd-variant--roomy">
               <div className="wd-split wd-split--ring">
                  <Ring percent={percent} answered={answered} appeared={appeared} />
                  <div className="wd-facts wd-facts--rows">
                     <div className="wd-fact wd-fact--ok">
                        <span>Corecte</span>
                        <strong>{correct}</strong>
                     </div>
                     <div className="wd-fact wd-fact--bad">
                        <span>Greșite</span>
                        <strong>{wrong}</strong>
                     </div>
                     <div className="wd-fact">
                        <span>Răspunsuri</span>
                        <strong>{answered}</strong>
                     </div>
                  </div>
               </div>
            </div>
         </Tile>
      );
   }

   return (
      <Tile id="accuracy" size={size}>
         <Head icon={examIcon} title="Întrebări" action={{ to: "/student/test", label: "Exersează" }} />
         <div className="wd-split wd-split--ring">
            <Ring percent={percent} answered={answered} appeared={appeared} />
            <div className="wd-facts wd-facts--rows">
               <div className="wd-fact wd-fact--ok">
                  <span>Corecte</span>
                  <strong>
                     <CountUp value={correct} />
                  </strong>
               </div>
               <div className="wd-fact wd-fact--bad">
                  <span>Greșite</span>
                  <strong>
                     <CountUp value={wrong} />
                  </strong>
               </div>
               <div className="wd-fact">
                  <span>Răspunsuri</span>
                  <strong>
                     <CountUp value={answered} />
                  </strong>
               </div>
            </div>
         </div>
      </Tile>
   );
}

/* ================= bilete și teme ================= */

function GroupRow({ label, group, appeared, legend }) {
   return (
      <div className="wd-group">
         <div className="wd-group__head">
            <span className="wd-group__label">{label}</span>
            <span className="wd-group__value">
               <strong>
                  <CountUp value={group.passed} />
               </strong>{" "}
               / {group.total}
            </span>
         </div>
         <ProgressSegments
            className="wd-group__bar"
            green={appeared ? group.passed : 0}
            red={appeared ? group.errors : 0}
            total={group.total}
            label={`${group.passed} trecute, ${group.errors} cu greșeli din ${group.total}`}
         />
         {legend && (
            <div className="wd-group__legend">
               <span className="wd-legend-item wd-legend-item--ok">Trecute: {group.passed}</span>
               <span className="wd-legend-item wd-legend-item--bad">Cu greșeli: {group.errors}</span>
               <span className="wd-legend-item">Neîncepute: {group.untouched}</span>
            </div>
         )}
      </div>
   );
}

function PracticeWidget({ size, data }) {
   const { practice } = data;
   const appeared = useAppeared(260);
   const tickets = groupByState(practice?.tickets);
   const topics = groupByState(practice?.categories);

   if (!practice) {
      return (
         <Tile id="practice" size={size}>
            <Head icon={bookIcon} title="Bilete și teme" />
            <Skeleton />
         </Tile>
      );
   }

   if (size === "1x1") {
      return (
         <Tile id="practice" size={size} to="/student/test" label={`Bilete trecute: ${tickets.passed} din ${tickets.total}`}>
            <Head icon={bookIcon} title="Bilete și teme" />
            <div className="wd-variant wd-variant--small">
               <div className="wd-big">
                  <span className="wd-big__num">
                     <CountUp value={tickets.passed} />
                  </span>
                  <span className="wd-big__of">/ {tickets.total}</span>
               </div>
               <ProgressSegments
                  className="wd-group__bar"
                  green={appeared ? tickets.passed : 0}
                  red={appeared ? tickets.errors : 0}
                  total={tickets.total}
                  label={`${tickets.passed} trecute, ${tickets.errors} cu greșeli din ${tickets.total}`}
               />
               <span className="wd-caption">bilete trecute</span>
            </div>
            <div className="wd-variant wd-variant--roomy wd-groups">
               <GroupRow label="Bilete" group={tickets} appeared={appeared} legend />
               <GroupRow label="Teme" group={topics} appeared={appeared} legend />
            </div>
         </Tile>
      );
   }

   return (
      <Tile id="practice" size={size}>
         <Head icon={bookIcon} title="Bilete și teme" action={{ to: "/student/test", label: "Exersează" }} />
         <div className="wd-groups">
            <GroupRow label="Bilete" group={tickets} appeared={appeared} legend={size === "2x2"} />
            <GroupRow label="Teme" group={topics} appeared={appeared} legend={size === "2x2"} />
         </div>
      </Tile>
   );
}

/* ================= spre permis ================= */

function formatShortDate(value) {
   const d = value ? new Date(value) : null;
   if (!d || Number.isNaN(d.getTime())) return "";
   return d.toLocaleDateString("ro-RO", { day: "numeric", month: "short" }).replaceAll(".", "");
}

/**
 * Pașii până la permis, în ordine: biletele, examenul teoretic, lecțiile
 * practice, examenul practic. Pasul curent e primul neîncheiat.
 */
function pathSteps({ track, practice, theory, practical }) {
   const { stats } = track;
   const tickets = groupByState(practice?.tickets);
   const last = theory.last;

   let theoryTone = "none";
   let theoryText = theory.loaded ? "Nesusținut" : "…";
   if (last?.inProgress) {
      theoryTone = "live";
      theoryText = "În desfășurare";
   } else if (last) {
      theoryTone = last.passed ? "ok" : "bad";
      theoryText = `${last.passed ? "Admis" : "Respins"} · ${last.score}/${last.total}`;
   }

   const practicalStatus = String(practical?.cardItem?.statusLabel || "").trim();
   const practicalPassed = practicalStatus === "Admis";
   const practicalTone = !practical
      ? "none"
      : practicalPassed
        ? "ok"
        : ["Respins", "Absent", "Anulat"].includes(practicalStatus)
          ? "bad"
          : "live";

   return [
      {
         key: "tickets",
         title: "Bilete de practică",
         short: "Bilete",
         sub: practice
            ? tickets.errors > 0
               ? `${roCount(tickets.errors, "bilet", "bilete")} cu greșeli`
               : "Fiecare bilet, fără greșeli"
            : "Se încarcă…",
         done: Boolean(practice) && tickets.total > 0 && tickets.passed === tickets.total,
         status: practice ? `${tickets.passed}/${tickets.total}` : "…",
         to: "/student/test",
      },
      {
         key: "theory",
         title: "Examen teoretic",
         short: "Teoretic",
         sub: last
            ? `${formatShortDate(last.at)} · ${roCount(theory.attempts, "încercare", "încercări")}`
            : "Testul de la școală",
         done: Boolean(last?.passed),
         status: theoryText,
         tone: theoryTone,
         to: "/student/exam",
      },
      {
         key: "lessons",
         title: "Lecții practice",
         short: "Lecții",
         sub:
            stats.todo > 0
               ? `${roCount(stats.todo, "lecție", "lecții")} de programat`
               : roCount(stats.scheduled, "lecție programată", "lecții programate"),
         done: stats.done >= stats.total,
         status: `${stats.done}/${stats.total}`,
         to: "/student/calendar",
      },
      {
         key: "practical",
         title: "Examen practic",
         short: "Practic",
         sub: practical
            ? `${formatShortDate(practical.start)}, ${formatTime(practical.start)}`
            : "Alegi data când primești acces",
         done: practicalPassed,
         status: practical ? practicalStatus || "Programat" : "Neprogramat",
         tone: practicalTone,
         popup: "practicalExamBooking",
      },
   ];
}

function StepLink({ step, className, children }) {
   return step.to ? (
      <Link to={step.to} className={className}>
         {children}
      </Link>
   ) : (
      <button type="button" className={className} onClick={() => openPopup(step.popup)}>
         {children}
      </button>
   );
}

function PathWidget({ size, data }) {
   const steps = pathSteps(data);
   const currentIndex = steps.findIndex((step) => !step.done);
   const doneCount = steps.filter((step) => step.done).length;
   const stateOf = (step, index) =>
      step.done ? "done" : index === currentIndex ? "current" : "pending";
   const toneOf = (step, state) => step.tone || (step.done ? "ok" : state === "current" ? "live" : "none");
   const mark = (step, index) =>
      step.done ? (
         <svg viewBox="0 0 24 24">
            <path d={CHECK_PATH} />
         </svg>
      ) : (
         index + 1
      );

   return (
      <Tile id="path" size={size}>
         <Head
            icon={carIcon}
            title="Spre permis"
            extra={
               <span className="wd__muted">
                  {doneCount} / {steps.length}
               </span>
            }
         />
         {size === "2x1" ? (
            <ol className="wd-pathline">
               {steps.map((step, index) => {
                  const state = stateOf(step, index);
                  return (
                     <li key={step.key} className={`wd-pathline__step is-${state}`}>
                        <StepLink step={step} className="wd-pathline__link">
                           <span className="wd-step-mark" aria-hidden="true">
                              {mark(step, index)}
                           </span>
                           <strong className="wd-small-only">{step.short}</strong>
                           <strong className="wd-roomy">{step.title}</strong>
                           <span className="wd-roomy wd-pathline__sub">{step.sub}</span>
                           <span className={`wd-status wd-status--${toneOf(step, state)}`}>{step.status}</span>
                        </StepLink>
                     </li>
                  );
               })}
            </ol>
         ) : (
            <ol className="wd-steps">
               {steps.map((step, index) => {
                  const state = stateOf(step, index);
                  return (
                     <li key={step.key} className={`wd-step is-${state}`} style={{ "--k": index }}>
                        <span className="wd-step-mark" aria-hidden="true">
                           {mark(step, index)}
                        </span>
                        <StepLink step={step} className="wd-step__body">
                           <span className="wd-step__text">
                              <strong>{step.title}</strong>
                              <span>{step.sub}</span>
                           </span>
                           <span className={`wd-status wd-status--${toneOf(step, state)}`}>{step.status}</span>
                        </StepLink>
                     </li>
                  );
               })}
            </ol>
         )}
      </Tile>
   );
}

/* ================= videoclipuri ================= */

// miniatura 16:9 fără benzi negre (hq720); unele videoclipuri n-o au: mqdefault
function VideoImage({ video }) {
   return (
      <img
         className="wd-video__img"
         src={youtubeThumbnailUrl(video.youtubeId, "hq720")}
         alt=""
         loading="lazy"
         onError={(event) => {
            const fallback = youtubeThumbnailUrl(video.youtubeId, "mqdefault");
            if (event.currentTarget.src !== fallback) event.currentTarget.src = fallback;
         }}
      />
   );
}

function VideosWidget({ size, data }) {
   const { loaded, total, items } = data.videos;
   const [featured, ...rest] = items;

   if (!loaded) {
      return (
         <Tile id="videos" size={size}>
            <Head icon={playIcon} title="Videoclipuri" />
            <Skeleton />
         </Tile>
      );
   }

   if (size !== "2x2" || !featured) {
      return (
         <Tile
            id="videos"
            size={size}
            to="/student/videoclipuri"
            className={featured ? "wd-videos--cover" : "wd-videos--empty"}
            label="Videoclipuri"
         >
            {featured && <VideoImage video={featured} />}
            <span className="wd-video__badge">
               Videoclipuri{total > 0 ? ` · ${total}` : ""}
            </span>
            <span className="wd-video__play" aria-hidden="true">
               <svg viewBox="0 0 24 24">
                  <path d={PLAY_PATH} />
               </svg>
            </span>
            {featured && <span className="wd-video__title">{featured.title}</span>}
         </Tile>
      );
   }

   return (
      <Tile id="videos" size={size}>
         <Head
            icon={playIcon}
            title="Videoclipuri"
            extra={total > 0 ? <span className="wd__muted">{total}</span> : null}
            action={{ to: "/student/videoclipuri", label: "Toate" }}
         />
         <div className="wd-videos">
            <Link to="/student/videoclipuri" className="wd-video wd-video--featured">
               <VideoImage video={featured} />
               <span className="wd-video__play" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                     <path d={PLAY_PATH} />
                  </svg>
               </span>
               <span className="wd-video__title">{featured.title}</span>
            </Link>
            {rest.length > 0 && (
               <div className="wd-videos__more">
                  {rest.map((video) => (
                     <Link key={video.id} to="/student/videoclipuri" className="wd-video wd-video--small" title={video.title}>
                        <VideoImage video={video} />
                        <span className="wd-video__title">{video.title}</span>
                     </Link>
                  ))}
               </div>
            )}
         </div>
      </Tile>
   );
}

/* ================= alegerea widget-ului ================= */

const RENDERERS = {
   welcome: WelcomeWidget,
   actions: ActionsWidget,
   journey: JourneyWidget,
   lessons: LessonsWidget,
   accuracy: AccuracyWidget,
   practice: PracticeWidget,
   path: PathWidget,
   videos: VideosWidget,
   next: NextLessonWidget,
   weather: WeatherWidget,
   notifications: NotificationsWidget,
   exams: ExamsWidget,
   mistakes: MistakesWidget,
   goal: GoalWidget,
   examTrend: ExamTrendWidget,
   activity: ({ size, data }) => <ActivityWidget size={size} activity={data.activity} />,
   progress: ({ size, data }) => <ProgressWidget size={size} activity={data.activity} />,
};

export function renderHomeWidget(item, data) {
   const Widget = RENDERERS[item.id];
   return Widget ? <Widget size={item.size} data={data} /> : null;
}
